import { cva } from "cva";
import {
  DateField as AriaDateField,
  DateFieldProps as AriaDateFieldProps,
  DateInput as AriaDateInput,
  DateInputProps as AriaDateInputProps,
  composeRenderProps,
  DateSegment,
  DateValue,
  I18nProvider,
  ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Description, FieldError, Label } from "./Field";
import { composeTailwindRenderProps } from "./utils";

export interface DateFieldProps<T extends DateValue>
  extends AriaDateFieldProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  ref?: React.RefObject<HTMLInputElement | null>;
  dateInputProps?: Omit<DateInputProps, "children" | "ref" | "isFocused">;
}

export function DateField<T extends DateValue>({
  label,
  description,
  errorMessage,
  ref,
  ...props
}: DateFieldProps<T>) {
  return (
    <I18nProvider locale="da-DK">
      <AriaDateField
        ref={ref}
        {...props}
        className={composeTailwindRenderProps(
          props.className,
          "flex flex-col gap-1",
        )}
      >
        {label && <Label>{label}</Label>}
        <DateInput {...props.dateInputProps} />
        {description && <Description>{description}</Description>}
        <FieldError>{errorMessage}</FieldError>
      </AriaDateField>
    </I18nProvider>
  );
}

const segmentStyles = cva({
  base: "inline py-2 px-1 type-literal:px-0 rounded-sm outline-none caret-transparent uppercase font-bold text-sm text-theme-on-background",
  variants: {
    isPlaceholder: {
      true: "text-theme-on-background-variant",
    },
    isFocused: {
      true: "bg-theme-on-background-hover",
    },
  },
});

type DateInputProps = Omit<AriaDateInputProps, "children"> & {
  ref?: React.RefObject<HTMLInputElement | null>;
};

export function DateInput(props: DateInputProps) {
  return (
    <I18nProvider locale="da-DK">
      <AriaDateInput
        {...props}
        className={composeRenderProps(props.className, (className) =>
          twMerge("-mx-1", className),
        )}
      >
        {(segment) => (
          <DateSegment segment={segment} className={segmentStyles} />
        )}
      </AriaDateInput>
    </I18nProvider>
  );
}
